import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogHeaderImage } from 'foundations-components/transitional/components/DialogHeaderImage';
import { H3 } from 'foundations-components/transitional/components/H3';
import { Image } from 'foundations-components/transitional/components/Image';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Media, MediaBody, MediaRight } from 'foundations-components/transitional/components/Media';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EmbeddedComponent = ({
  children
}) => /*#__PURE__*/_jsx(Flex, {
  blockSize: "100%",
  flexDirection: "column",
  children: children
});
const propTypes = {
  allowHeader: PropTypes.bool.isRequired,
  bodyClassName: PropTypes.string,
  cancelHref: PropTypes.string,
  cancelTarget: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  confirmDisabledTooltip: PropTypes.node,
  confirmHref: PropTypes.string,
  confirmLabel: PropTypes.node,
  confirmTarget: PropTypes.string.isRequired,
  confirmUse: PropTypes.string,
  'data-selenium-test': PropTypes.string,
  dialogClassName: PropTypes.string,
  extraFooter: PropTypes.node,
  extraRightAlignedFooter: PropTypes.node,
  footerAlign: PropTypes.oneOf(['left', 'center', 'right', 'between']).isRequired,
  headerImage: PropTypes.string,
  headerImageOffsetBottom: PropTypes.number,
  headerImageOffsetTop: PropTypes.number,
  includeFooter: PropTypes.bool,
  isConfirmButtonLoading: PropTypes.bool,
  confirmProps: PropTypes.object,
  moreButtons: PropTypes.node,
  offsetTop: PropTypes.number,
  onCloseComplete: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onScroll: PropTypes.func,
  panelClassName: PropTypes.string,
  rejectLabel: PropTypes.node,
  showCancelButton: PropTypes.bool.isRequired,
  showCloseButton: PropTypes.bool.isRequired,
  showConfirmButton: PropTypes.bool.isRequired,
  showExtraFooterFirst: PropTypes.bool,
  size: PropTypes.oneOf(['auto', 'medium', 'short', 'tall']),
  title: PropTypes.node,
  use: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(['embedded', 'panel', 'sidebar'])]),
  width: PropTypes.number.isRequired
};
const defaultProps = {
  allowHeader: true,
  cancelTarget: '_self',
  confirmTarget: '_self',
  confirmUse: 'primary',
  footerAlign: 'left',
  includeFooter: true,
  isConfirmButtonLoading: false,
  onConfirm: emptyFunction,
  onReject: emptyFunction,
  onCloseComplete: emptyFunction,
  showCancelButton: true,
  showCloseButton: true,
  showConfirmButton: true,
  showExtraFooterFirst: false,
  size: 'auto',
  use: 'default',
  width: 500
};
function bodyClassNameToStyleProps(className) {
  switch (className) {
    case 'display-flex align-center justify-center':
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      };
    case 'p-x-0 p-top-0':
      return {
        paddingBlockStart: '0',
        paddingInline: '0'
      };
    default:
      return undefined;
  }
}
class BaseDialog extends PureComponent {
  getTitleNode() {
    let titleNode = this.props.title;
    if (typeof titleNode === 'string') {
      titleNode = /*#__PURE__*/_jsx(H3, {
        marginBlock: "0",
        marginInline: "0",
        children: titleNode
      }, "title-node");
    }
    return titleNode;
  }
  getCloseButton() {
    const {
      showCloseButton,
      onReject
    } = this.props;
    if (!showCloseButton) {
      return null;
    }
    return /*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onReject,
      "data-selenium-test": "base-dialog-close-btn"
    }, "close-button");
  }
  renderHeader() {
    const closeButton = this.getCloseButton();
    const {
      showCloseButton,
      allowHeader,
      use
    } = this.props;
    const titleNode = this.getTitleNode();
    if (!showCloseButton && !titleNode || !allowHeader) {
      return null;
    }
    if (use === 'panel') {
      return /*#__PURE__*/_jsxs(PanelHeader, {
        children: [titleNode, closeButton]
      });
    }
    return /*#__PURE__*/_jsxs(DialogHeader, {
      flexShrink: "0",
      children: [titleNode, closeButton]
    });
  }
  renderBody() {
    const {
      allowHeader,
      children,
      bodyClassName,
      use
    } = this.props;
    const isEmbedded = use === 'embedded';
    if (!children) {
      return null;
    }
    if (use === 'panel') {
      return /*#__PURE__*/_jsxs(PanelBody, Object.assign({}, bodyClassNameToStyleProps(bodyClassName), {
        children: [!allowHeader && (this.getTitleNode() || this.getCloseButton()) && /*#__PURE__*/_jsxs(PanelSection, {
          children: [this.getTitleNode(), this.getCloseButton()]
        }), /*#__PURE__*/_jsx(PanelSection, {
          children: children
        })]
      }));
    }
    return /*#__PURE__*/_jsxs(DialogBody, Object.assign({}, bodyClassNameToStyleProps(bodyClassName), {
      overflowX: isEmbedded ? 'hidden' : undefined,
      overflowY: isEmbedded ? 'auto' : undefined,
      paddingBlockEnd: isEmbedded ? '0' : undefined,
      children: [!allowHeader && (this.getTitleNode() || this.getCloseButton()) && /*#__PURE__*/_jsxs(_Fragment, {
        children: [this.getTitleNode(), this.getCloseButton()]
      }), this.renderHeaderImage(), children]
    }));
  }
  renderFooter() {
    const {
      includeFooter,
      moreButtons,
      extraFooter,
      extraRightAlignedFooter,
      footerAlign,
      showExtraFooterFirst,
      use
    } = this.props;
    const confirmButton = this.renderConfirmButton();
    const cancelButton = this.renderCancelButton();
    if (!includeFooter) {
      return null;
    }
    if (!(confirmButton || cancelButton || moreButtons || extraFooter)) {
      return null;
    }
    const Component = use === 'panel' ? PanelFooter : DialogFooter;
    return /*#__PURE__*/_jsx(Component, {
      align: footerAlign,
      paddingBlockStart: "400",
      flexShrink: use === 'embedded' ? '0' : undefined,
      children: /*#__PURE__*/_jsxs(Media, {
        align: "center",
        children: [/*#__PURE__*/_jsxs(MediaBody, {
          children: [showExtraFooterFirst && /*#__PURE__*/_jsx(View, {
            paddingInlineStart: "100",
            children: extraFooter
          }), /*#__PURE__*/_jsx(Flex, {
            flexDirection: "row",
            flexWrap: "wrap",
            paddingBlockEnd: "100",
            children: use === 'sidebar' ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [moreButtons, !showExtraFooterFirst && extraFooter, /*#__PURE__*/_jsx(Box, {
                alignSelf: "end",
                children: confirmButton
              }), /*#__PURE__*/_jsx(Box, {
                alignSelf: "end",
                children: cancelButton
              })]
            }) : /*#__PURE__*/_jsxs(_Fragment, {
              children: [confirmButton, moreButtons, cancelButton, /*#__PURE__*/_jsx(View, {
                paddingInlineStart: "100",
                children: !showExtraFooterFirst && extraFooter
              })]
            })
          })]
        }), extraRightAlignedFooter && /*#__PURE__*/_jsx(MediaRight, {
          children: extraRightAlignedFooter
        })]
      })
    });
  }
  renderConfirmButton() {
    let component;
    const {
      onConfirm,
      confirmHref,
      confirmUse,
      confirmDisabled,
      confirmLabel,
      confirmDisabledTooltip,
      confirmTarget,
      showConfirmButton,
      isConfirmButtonLoading,
      confirmProps
    } = this.props;
    if (!showConfirmButton) {
      return null;
    }
    const ButtonComponent = isConfirmButtonLoading ? LoadingButton : Button;
    const button = /*#__PURE__*/_jsx(ButtonComponent, Object.assign({
      onClick: onConfirm,
      href: confirmHref,
      target: confirmTarget,
      use: confirmUse,
      disabled: confirmDisabled,
      "data-selenium-test": "base-dialog-confirm-btn",
      "data-confirm-button": "accept"
    }, confirmProps, {
      children: confirmLabel || I18n.text('customerDataUiUtilities.BaseDialog.save')
    }));
    if (confirmDisabledTooltip && confirmDisabled) {
      component = /*#__PURE__*/_jsx(Tooltip, {
        title: confirmDisabledTooltip,
        children: button
      });
    } else {
      component = button;
    }
    return component;
  }
  renderCancelButton() {
    const {
      onReject,
      cancelHref,
      rejectLabel,
      cancelTarget
    } = this.props;
    if (!this.props.showCancelButton) {
      return null;
    }
    let clickHandler = {
      onClick: onReject
    };
    if (cancelHref) {
      clickHandler = {};
    }
    return /*#__PURE__*/_jsx(Button, Object.assign({}, clickHandler, {
      "data-selenium-test": "base-dialog-cancel-button",
      use: "secondary",
      href: cancelHref,
      target: cancelTarget,
      onClick: onReject,
      children: rejectLabel || I18n.text('customerDataUiUtilities.BaseDialog.cancel')
    }));
  }
  renderHeaderImage() {
    const {
      headerImage,
      headerImageOffsetTop,
      headerImageOffsetBottom
    } = this.props;
    if (!headerImage) {
      return null;
    }
    return /*#__PURE__*/_jsx(DialogHeaderImage, {
      offsetTop: headerImageOffsetTop,
      offsetBottom: headerImageOffsetBottom,
      children: /*#__PURE__*/_jsx(Image, {
        src: headerImage,
        responsive: false,
        marginInline: "auto"
      })
    });
  }
  renderWrapper(content) {
    const {
      width,
      showCloseButton,
      onReject,
      use,
      dialogClassName,
      size,
      onOpenComplete,
      onScroll,
      onCloseComplete
    } = this.props;
    const commonProps = {
      'data-selenium-test': this.props['data-selenium-test'],
      size,
      width,
      paddingBlockEnd: dialogClassName === 'p-bottom-10' ? '1000' : undefined,
      onScroll
    };
    switch (use) {
      case 'sidebar':
        return /*#__PURE__*/_jsx(ModalPanel, Object.assign({}, commonProps, {
          size: size,
          onEsc: showCloseButton ? onReject : undefined,
          onOpenComplete: onOpenComplete,
          onCloseComplete: onCloseComplete,
          children: content
        }));
      case 'panel':
        return /*#__PURE__*/_jsx(Panel, Object.assign({}, commonProps, {
          children: content
        }));
      case 'embedded':
        return /*#__PURE__*/_jsx(EmbeddedComponent, {
          children: content
        });
      default:
        return /*#__PURE__*/_jsx(Modal, Object.assign({}, commonProps, {
          onEsc: showCloseButton ? onReject : undefined,
          onOpenComplete: onOpenComplete,
          onCloseComplete: onCloseComplete,
          children: content
        }));
    }
  }
  render() {
    return this.renderWrapper( /*#__PURE__*/_jsxs(_Fragment, {
      children: [this.renderHeader(), this.renderBody(), this.renderFooter()]
    }));
  }
}
BaseDialog.defaultProps = defaultProps;
BaseDialog.propTypes = propTypes;
export default BaseDialog;